import { addMonthsToMonthKey, localMonthKey } from './zoned.js';

/**
 * Gestion (03/10/2026) : périodes de l'espace « Gestion » (exercice fiscal, année civile, mois), calculées en
 * **mois locaux** du garage. Un exercice est désigné par l'année de son premier mois (`2026` avec un début en
 * septembre = septembre 2026 → août 2027). Fonctions pures, partagées par l'API et l'interface.
 */
export const PERIOD_KINDS = ['fiscal', 'year', 'month'] as const;
export type PeriodKindName = (typeof PERIOD_KINDS)[number];

const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const pad = (n: number) => String(n).padStart(2, '0');

export interface PeriodRange {
  kind: PeriodKindName;
  key: string;
  label: string;
  /** Premier mois `YYYY-MM` de la période. */
  firstMonth: string;
  /** Nombre de mois (12 ou 1). */
  months: number;
  /** Premier jour local (inclus) et dernier jour local (inclus), `YYYY-MM-DD`. */
  from: string;
  to: string;
}

/** Clé valide pour le type de période : `YYYY` (exercice, année) ou `YYYY-MM` (mois). */
export function isPeriodKey(kind: PeriodKindName, key: string): boolean {
  return kind === 'month' ? /^\d{4}-(0[1-9]|1[0-2])$/.test(key) : /^\d{4}$/.test(key);
}

function lastDayOfMonth(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${month}-${pad(new Date(Date.UTC(y, m, 0)).getUTCDate())}`;
}

/** Libellé : « Exercice 2026 » (début en janvier), « Exercice 2026-2027 », « Année 2026 », « Octobre 2026 ». */
export function periodLabel(kind: PeriodKindName, key: string, fiscalStartMonth: number): string {
  if (kind === 'year') return `Année ${key}`;
  if (kind === 'fiscal') return fiscalStartMonth === 1 ? `Exercice ${key}` : `Exercice ${key}-${Number(key) + 1}`;
  const name = MONTHS_FR[Number(key.slice(5, 7)) - 1] ?? '';
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${key.slice(0, 4)}`;
}

export function periodRange(kind: PeriodKindName, key: string, fiscalStartMonth: number): PeriodRange {
  const firstMonth = kind === 'month' ? key : `${key}-${pad(kind === 'fiscal' ? fiscalStartMonth : 1)}`;
  const months = kind === 'month' ? 1 : 12;
  return {
    kind,
    key,
    label: periodLabel(kind, key, fiscalStartMonth),
    firstMonth,
    months,
    from: `${firstMonth}-01`,
    to: lastDayOfMonth(addMonthsToMonthKey(firstMonth, months - 1)),
  };
}

/** Période précédente de même type (exercice ou année précédents, mois précédent). */
export function previousPeriodKey(kind: PeriodKindName, key: string): string {
  return kind === 'month' ? addMonthsToMonthKey(key, -1) : String(Number(key) - 1);
}

/** Clé de la période de type `kind` qui contient le mois local `month` (`YYYY-MM`). */
export function periodKeyOfMonth(kind: PeriodKindName, month: string, fiscalStartMonth: number): string {
  if (kind === 'month') return month;
  const y = Number(month.slice(0, 4));
  if (kind === 'year') return String(y);
  return String(Number(month.slice(5, 7)) >= fiscalStartMonth ? y : y - 1);
}

/** Période en cours (fuseau du garage). */
export function currentPeriodKey(kind: PeriodKindName, now: Date, timeZone: string, fiscalStartMonth: number): string {
  return periodKeyOfMonth(kind, localMonthKey(now, timeZone), fiscalStartMonth);
}

/** Mois `YYYY-MM` de la période, dans l'ordre. */
export function periodMonths(range: Pick<PeriodRange, 'firstMonth' | 'months'>): string[] {
  return Array.from({ length: range.months }, (_, i) => addMonthsToMonthKey(range.firstMonth, i));
}
